import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useAuthStore } from "@/stores/auth";
import { api, ApiError } from "@/lib/api";
import { analytics } from "@/lib/analytics";
import { SEO } from "@/components/SEO";

const plans = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    period: "forever",
    description: "Perfect for testing",
    features: ["1 domain", "1,000 messages/month", "7-day retention"],
    popular: false,
  },
  {
    id: "starter",
    name: "Starter",
    price: "$19",
    period: "/month",
    description: "For small businesses",
    features: ["5 domains", "100K messages/month", "90-day retention", "Email alerts"],
    popular: false,
  },
  {
    id: "pro",
    name: "Pro",
    price: "$49",
    period: "/month",
    description: "For growing companies",
    features: ["25 domains", "1M messages/month", "1-year retention", "Priority support"],
    popular: true,
  },
];

export function RegisterPage() {
  const [step, setStep] = useState<"account" | "plan">("account");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("free");
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const justRegistered = useRef(false);
  const navigate = useNavigate();
  const { user, setUser, setOrganizations } = useAuthStore();

  // Redirect already-logged-in users to dashboard (unless they just registered)
  useEffect(() => {
    if (user && !justRegistered.current) {
      navigate({ to: "/dashboard" });
    }
  }, [user, navigate]);

  const handleAccountSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const result = await api.auth.register({ name, email, password });
      justRegistered.current = true;
      setUser(result.user);
      setOrganizations([{ ...result.organization, role: "owner" }]);
      setOrganizationId(result.organization.id);
      analytics.registration();
      // Google Ads conversion tracking
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-17873660453/dh_6CMXsqOQbEKXU6cpC'
        });
      }
      setStep("plan");
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("An unexpected error occurred");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSubmit = async () => {
    if (selectedPlan === "free") {
      navigate({ to: "/dashboard" });
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result = await api.billing.createCheckout(organizationId!, selectedPlan) as { url: string };
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to start checkout. Please try again.");
      }
      setLoading(false);
    }
  };

  const handleSkipToFree = () => {
    navigate({ to: "/dashboard" });
  };

  return (
    <div className="flex min-h-screen">
      <SEO
        title="Create Account"
        description="Sign up for GetSent to start monitoring your DMARC reports and protect your domain from email spoofing. Free plan available."
        canonical="/register"
      />
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-500 via-purple-600 to-indigo-700 p-12 flex-col justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 backdrop-blur">
            <svg className="icon-lg text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <span className="text-2xl font-bold text-white">GetSent</span>
        </div>
        <div className="space-y-6">
          <h1 className="text-4xl font-bold text-white leading-tight">
            {step === "account" ? (
              <>Start protecting your<br />domain today</>
            ) : (
              <>Choose the plan<br />that fits your needs</>
            )}
          </h1>
          <p className="text-lg text-white/80 max-w-md">
            {step === "account" ? (
              "Stop worrying about email deliverability. Start monitoring."
            ) : (
              "Start free and upgrade anytime. All plans include our core DMARC monitoring features."
            )}
          </p>
          {step === "account" && (
            <div className="space-y-3 pt-4">
              <div className="flex items-center gap-3 text-white/90">
                <svg className="icon-md text-emerald-300" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                <span>Free plan includes 1 domain</span>
              </div>
              <div className="flex items-center gap-3 text-white/90">
                <svg className="icon-md text-emerald-300" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                <span>Real-time DMARC analytics</span>
              </div>
              <div className="flex items-center gap-3 text-white/90">
                <svg className="icon-md text-emerald-300" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                <span>DNS tools included</span>
              </div>
              <div className="flex items-center gap-3 text-white/90">
                <svg className="icon-md text-emerald-300" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                <span>No credit card required</span>
              </div>
            </div>
          )}
        </div>
        <div className="text-sm text-white/60">
          {step === "account" ? "Setup takes less than 5 minutes" : "You can change plans anytime"}
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex flex-1 items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-2 mb-8">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600">
              <svg className="icon-lg text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-indigo-500 to-purple-600 bg-clip-text text-transparent">GetSent</span>
          </div>

          {step === "account" ? (
            <>
              <div className="text-center lg:text-left">
                <h2 className="text-2xl font-bold">Create your account</h2>
                <p className="mt-2 text-muted-foreground">
                  Get started with your free account
                </p>
              </div>

              <form onSubmit={handleAccountSubmit} className="space-y-5">
                {error && (
                  <div className="alert alert-error">
                    <svg className="icon-md shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                    </svg>
                    {error}
                  </div>
                )}

                <div>
                  <label htmlFor="name" className="block text-sm font-medium mb-2">
                    Full name
                  </label>
                  <input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    placeholder="John Doe"
                    className="input input-primary"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-2">
                    Work email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="you@company.com"
                    className="input input-primary"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium mb-2">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    placeholder="Create a strong password"
                    className="input input-primary"
                  />
                  <p className="mt-2 text-xs text-muted-foreground">
                    Must be at least 8 characters
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-primary btn-lg w-full"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating account...
                    </span>
                  ) : (
                    "Continue"
                  )}
                </button>

                <p className="text-xs text-center text-muted-foreground">
                  By signing up, you agree to our{" "}
                  <Link to="/terms" className="text-indigo-600 hover:text-indigo-500">Terms of Service</Link>
                  {" "}and{" "}
                  <Link to="/privacy" className="text-indigo-600 hover:text-indigo-500">Privacy Policy</Link>
                </p>
              </form>

              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{" "}
                <Link to="/login" className="font-medium text-indigo-600 hover:text-indigo-500">
                  Sign in
                </Link>
              </p>
            </>
          ) : (
            <>
              <div className="text-center lg:text-left">
                <h2 className="text-2xl font-bold">Choose your plan</h2>
                <p className="mt-2 text-muted-foreground">
                  Start free or unlock more features with a paid plan
                </p>
              </div>

              {error && (
                <div className="alert alert-error">
                  <svg className="icon-md shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                  </svg>
                  {error}
                </div>
              )}

              <div className="space-y-3">
                {plans.map((plan) => (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`w-full text-left rounded-xl border-2 p-4 transition-all ${
                      selectedPlan === plan.id
                        ? "border-indigo-600 bg-indigo-50 dark:bg-indigo-950/30"
                        : "border-border hover:border-indigo-300"
                    } ${plan.popular ? "ring-2 ring-indigo-600 ring-offset-2" : ""}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{plan.name}</span>
                          {plan.popular && (
                            <span className="text-xs bg-indigo-600 text-white px-2 py-0.5 rounded-full">
                              Popular
                            </span>
                          )}
                        </div>
                        <div className="mt-1 flex items-baseline gap-1">
                          <span className="text-2xl font-bold">{plan.price}</span>
                          <span className="text-sm text-muted-foreground">{plan.period}</span>
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">{plan.description}</p>
                        <ul className="mt-3 space-y-1">
                          {plan.features.map((feature) => (
                            <li key={feature} className="flex items-center gap-2 text-sm">
                              <svg className="h-4 w-4 text-indigo-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                              </svg>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                        selectedPlan === plan.id
                          ? "border-indigo-600 bg-indigo-600"
                          : "border-muted-foreground"
                      }`}>
                        {selectedPlan === plan.id && (
                          <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <button
                onClick={handlePlanSubmit}
                disabled={loading}
                className="btn btn-primary btn-lg w-full"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {selectedPlan === "free" ? "Starting..." : "Continue to payment..."}
                  </span>
                ) : selectedPlan === "free" ? (
                  "Start with Free"
                ) : (
                  `Continue with ${plans.find(p => p.id === selectedPlan)?.name}`
                )}
              </button>

              <button
                onClick={handleSkipToFree}
                className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Skip for now, I'll start with Free
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
